import clsx from "clsx";
import { Check } from "lucide-react";

import { k, t } from "../i18n/i18n";
import { FRIEND_BLUE } from "../map/units/symbols";
import { Motion } from "./presence";

/** Pastille d'étape partagée (16 px), devant le titre d'un `PanelGroup` (`leading`).
 *  - Vue Manœuvre (sans numéro) : coche bleu ami (faite), anneau au point qui respire doucement (en cours), anneau
 *    gris (à faire).
 *  - Données initiales (numérotée, `n` et `total`) : coche bleu ami (faite), anneau accent au chiffre accent (étape
 *    attendue), anneau gris (à remplir, à revoir, liste en chargement), anneau tireté (en attente), anneau clair
 *    (facultative). Chiffre en `text-caps`, chiffres tabulaires, demi-gras.
 *  Le changement d'état apparaît en douceur (`Motion`, clé = état) ; `aria-label` : « Étape 3 sur 7 : À remplir ». */

/** États de la pastille de la vue Manœuvre. */
export type StepProgress = "done" | "active" | "todo";
/** États de la pastille numérotée (`next` : l'étape attendue ; `pending` : numéro seul). */
export type NumberedStep = "done" | "next" | "todo" | "review" | "waiting" | "optional" | "pending";

const PROGRESS_LABEL: Record<StepProgress, string> = {
  done: k("Étape faite"),
  active: k("Étape en cours"),
  todo: k("Étape à faire"),
};

/** État lu après « Étape n sur N : » ; `waiting` prend de préférence la raison de l'attente (`status`). */
const NUMBERED_LABEL: Record<Exclude<NumberedStep, "pending">, string> = {
  done: k("Faite"),
  next: k("À remplir"),
  todo: k("À remplir"),
  review: k("À revoir"),
  waiting: k("En attente"),
  optional: k("Facultative"),
};

/** Anneau et chiffre de chaque état numéroté (hors `done`, disque plein). */
const RING: Record<Exclude<NumberedStep, "done">, string> = {
  next: "border-accent text-accent",
  todo: "border-line-strong text-ink-muted",
  review: "border-line-strong text-ink-muted",
  pending: "border-line-strong text-ink-muted",
  waiting: "border-dashed border-line-strong text-ink-muted",
  optional: "border-line text-ink-muted",
};

type StepMarkProps =
  | { state: StepProgress; n?: undefined; total?: undefined; status?: undefined }
  | {
      state: NumberedStep;
      n: number;
      total: number;
      /** Raison d'une attente, déjà traduite (« En attente du lieu ») : lue à la place de « En attente ». */
      status?: string;
    };

export function StepMark(props: StepMarkProps) {
  if (props.n === undefined) return <ProgressMark state={props.state} />;
  const { state, n, total, status } = props;
  const word = state === "pending" ? null : state === "waiting" && status ? status : t(NUMBERED_LABEL[state]);
  const label = word === null ? t("Étape {n} sur {total}", { n, total }) : t("Étape {n} sur {total} : {etat}", { n, total, etat: word });
  if (state === "done") {
    return (
      <Motion
        key="done"
        origin="zoom"
        role="img"
        aria-label={label}
        className="flex size-4 shrink-0 items-center justify-center rounded-full"
        style={{ background: FRIEND_BLUE }}
      >
        <Check size={12} strokeWidth={3} className="text-white" />
      </Motion>
    );
  }
  return (
    <Motion
      key={state}
      origin="fade"
      role="img"
      aria-label={label}
      className={clsx(
        "flex size-4 shrink-0 items-center justify-center rounded-full border-[1.5px] text-caps font-semibold tabular-nums leading-none",
        RING[state],
      )}
    >
      {n}
    </Motion>
  );
}

/** Pastille de la vue Manœuvre : coche bleu ami (faite), anneau animé doux (en cours), anneau gris (à faire). */
function ProgressMark({ state }: { state: StepProgress }) {
  const label = t(PROGRESS_LABEL[state]);
  if (state === "done") {
    return (
      <Motion
        key="done"
        origin="zoom"
        role="img"
        aria-label={label}
        className="flex size-4 shrink-0 items-center justify-center rounded-full"
        style={{ background: FRIEND_BLUE }}
      >
        <Check size={12} strokeWidth={3} className="text-white" />
      </Motion>
    );
  }
  if (state === "active") {
    // Anneau fixe et point qui respire doucement : « en cours », pas « chargement » (pas de rotation).
    return (
      <Motion
        key="active"
        origin="zoom"
        role="img"
        aria-label={label}
        className="flex size-4 shrink-0 items-center justify-center rounded-full border-[1.5px] border-accent"
      >
        <span className="size-1.5 animate-pulse rounded-full bg-accent motion-reduce:animate-none" />
      </Motion>
    );
  }
  return <span role="img" aria-label={label} className="size-4 shrink-0 rounded-full border-[1.5px] border-line-strong" />;
}
